<template>
  <div data-component="Filter" class="bg-white min-h-screen">
    <div class="flex justify-end">
      <CloseButton :onClose="goBack" />
    </div>
    <div class="flex justify-center">
      <h1 class="filter-title">Filtrez les résultats</h1>
    </div>
    <form @submit="submit">
      <div class="flex justify-center">
        <div class="flex filter-main-container">
          <div class="flex-1 pr-3 filter-border-right">
            <h4 class="filter-section-title">Gestion du lot</h4>
            <div class="checkboxes">
              <div class="checkbox-field">
                <div class="checkbox-widget" data-component="Widget/Checkbox">
                  <input 
                    type="checkbox" 
                    id="has-planification"
                    @change="togglePlanification"
                    v-model="filters.hasPlanification"
                    class="sr-only"
                  />
                  <label for="has-planification" class="custom-checkbox"></label>
                </div>
                <label for="has-planification" class="checkbox-label uppercase">Planifié</label>
              </div>
              <div :class="['no-bordered', { 'hidden': !isPlanificationOpen }]">
                <div :class="['range-date', { 'hidden': !isPlanificationOpen }]">
                  <div class="flex items-center gap-2 mb-4">
                    <label for="start-planification" class="date-label">Du :</label>
                    <div class="datepicker-input" data-component="Widget/DatePickerInput">
                      <label for="start-planification" class="date-display">
                        {{ startPlanificationAt ? formatDate(startPlanificationAt) : '' }}
                      </label>
                      <input 
                        type="date" 
                        class="date-input" 
                        @change="changeStartPlanification" 
                        :value="startPlanificationAt ? formatDateForInput(startPlanificationAt) : ''" 
                        id="start-planification" 
                      />
                      <i 
                        v-if="startPlanificationAt" 
                        class="ico close cursor-pointer ml-auto" 
                        @click="resetStartPlanification"
                      ></i>
                    </div>
                  </div>
                  <div class="flex items-center gap-2">
                    <label for="end-planification" class="date-label">Au :</label>
                    <div class="datepicker-input" data-component="Widget/DatePickerInput">
                      <label for="end-planification" class="date-display">
                        {{ endPlanificationAt ? formatDate(endPlanificationAt) : '' }}
                      </label>
                      <input 
                        type="date" 
                        class="date-input" 
                        @change="changeEndPlanification" 
                        :value="endPlanificationAt ? formatDateForInput(endPlanificationAt) : ''" 
                        id="end-planification" 
                      />
                      <i 
                        v-if="endPlanificationAt" 
                        class="ico close cursor-pointer ml-auto" 
                        @click="resetEndPlanification"
                      ></i>
                    </div>
                  </div>
                </div>
              </div>
              <div class="checkbox-field">
                <div class="checkbox-widget" data-component="Widget/Checkbox">
                  <input 
                    type="checkbox" 
                    id="has-non-planification"
                    v-model="filters.hasNotPlanification"
                    class="sr-only"
                  />
                  <label for="has-non-planification" class="custom-checkbox"></label>
                </div>
                <label for="has-non-planification" class="checkbox-label uppercase">Non-planifié</label>
              </div>
              <div class="checkbox-field">
                <label class="font-bold uppercase">A visiter avant</label>
              </div>
              <div class="no-bordered">
                <div class="range-date">
                  <div class="flex items-center gap-2 mb-4">
                    <label for="start-scheduledvisitat" class="date-label">Du :</label>
                    <div class="datepicker-input" data-component="Widget/DatePickerInput">
                      <label for="start-scheduledvisitat" class="date-display">
                        {{ startScheduledVisitAt ? formatDate(startScheduledVisitAt) : '' }}
                      </label>
                      <input 
                        type="date" 
                        class="date-input" 
                        @change="changeStartScheduledVisitAt" 
                        :value="startScheduledVisitAt ? formatDateForInput(startScheduledVisitAt) : ''" 
                        id="start-scheduledvisitat" 
                      />
                      <i 
                        v-if="startScheduledVisitAt" 
                        class="ico close cursor-pointer ml-auto" 
                        @click="resetStartScheduledVisitAt"
                      ></i>
                    </div>
                  </div>
                  <div class="flex items-center gap-2">
                    <label for="end-scheduledvisitat" class="date-label">Au :</label>
                    <div class="datepicker-input" data-component="Widget/DatePickerInput">
                      <label for="end-scheduledvisitat" class="date-display">
                        {{ endScheduledVisitAt ? formatDate(endScheduledVisitAt) : '' }}
                      </label>
                      <input 
                        type="date" 
                        class="date-input" 
                        @change="changeEndScheduledVisitAt" 
                        :value="endScheduledVisitAt ? formatDateForInput(endScheduledVisitAt) : ''" 
                        id="end-scheduledvisitat" 
                      />
                      <i 
                        v-if="endScheduledVisitAt" 
                        class="ico close cursor-pointer ml-auto" 
                        @click="resetEndScheduledVisitAt"
                      ></i>
                    </div>
                  </div>
                </div>
              </div>
              <div class="checkbox-field">
                <div class="checkbox-widget" data-component="Widget/Checkbox">
                  <input 
                    type="checkbox" 
                    id="has-hors-scaprim"
                    v-model="filters.hasHorsScaprim"
                    class="sr-only"
                  />
                  <label for="has-hors-scaprim" class="custom-checkbox"></label>
                </div>
                <label for="has-hors-scaprim" class="checkbox-label uppercase">Gestion hors Scaprim</label>
              </div>
            </div>
            <div class="mb-4">
              <label for="select-team" class="filter-label">Agence</label>
              <div class="relative">
                <select 
                  id="select-team" 
                  @change="changeColor"
                  :class="['filter-select', { 'is-light-blue': filters.team }]" 
                  data-name="team" 
                  v-model="filters.team"
                >
                  <option value="">Tous</option>
                  <option 
                    v-for="team in teams" 
                    :key="team.value" 
                    :value="team.value"
                  >
                    {{ team.label }}
                  </option>
                </select>
              </div>
            </div>
            <div class="mb-4 mt-5">
              <label for="select-manager" class="filter-label">Gestionnaires attribués</label>
              <div class="relative">
                <select 
                  id="select-manager" 
                  @change="changeColor" 
                  :class="['filter-select', { 'is-light-blue': filters.manager }]" 
                  data-name="manager" 
                  v-model="filters.manager"
                >
                  <option value="">Tous</option>
                  <option 
                    v-for="manager in managers" 
                    :key="manager.value" 
                    :value="manager.value"
                  >
                    {{ manager.label }}
                  </option>
                </select>
              </div>
            </div>
          </div>
          <div class="flex-1 pl-3" style="flex: 0 0 66.666%;">
            <h4 class="filter-section-title">Caractéristiques du lot</h4>

            <div class="relative mb-4">
              <input 
                class="filter-input" 
                data-name="nsil" 
                type="text" 
                placeholder="NSIL" 
                v-model="filters.nsil"
              />
              <span class="absolute left-3" style="top: calc(1/2 * 25%);">
                <i class="ico search"></i>
              </span>
            </div>

            <div class="relative mb-4">
              <input 
                class="filter-input" 
                data-name="city" 
                type="text" 
                placeholder="Ville, Code Postal ..." 
                v-model="filters.city"
              />
              <span class="absolute left-3" style="top: calc(1/2 * 25%);">
                <i class="ico search"></i>
              </span>
            </div>

            <div class="flex gap-6 m-0">
              <div class="flex-1 pr-0">
                <div class="mb-4">
                  <label for="select-property-type" class="filter-label">Type</label>
                  <div class="relative">
                    <select 
                      id="select-property-type" 
                      @change="changeColor" 
                      :class="['filter-select', { 'is-light-blue': filters.propertyType }]" 
                      data-name="propertyType" 
                      v-model="filters.propertyType"
                    >
                      <option value="">Tous</option>
                      <option 
                        v-for="propertyType in propertyTypes" 
                        :key="propertyType.value" 
                        :value="propertyType.value"
                      >
                        {{ propertyType.label }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="mb-4">
                  <label for="select-assistance" class="filter-label">Mandat cat</label>
                  <div class="relative">
                    <select 
                      id="select-assistance" 
                      @change="changeColor" 
                      :class="['filter-select', { 'is-light-blue': filters.assistance }]" 
                      data-name="assistance" 
                      v-model="filters.assistance"
                    >
                      <option value="">Tous</option>
                      <option 
                        v-for="assistance in assistances" 
                        :key="assistance.value" 
                        :value="assistance.value"
                      >
                        {{ assistance.label }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="mb-4">
                  <label for="select-owner" class="filter-label">Propriétaire</label>
                  <div class="relative">
                    <select 
                      id="select-owner" 
                      @change="changeColor" 
                      :class="['filter-select', { 'is-light-blue': filters.owner }]" 
                      data-name="owner" 
                      v-model="filters.owner"
                    >
                      <option value="">Tous</option>
                      <option 
                        v-for="owner in owners" 
                        :key="owner.value" 
                        :value="owner.value"
                      >
                        {{ owner.label }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="mb-4">
                  <label for="select-sub-unit" class="filter-label">Sous Unité</label>
                  <div class="relative">
                    <select 
                      id="select-sub-unit" 
                      @change="changeColor" 
                      :class="['filter-select', { 'is-light-blue': filters.subUnit }]" 
                      data-name="subUnit" 
                      v-model="filters.subUnit"
                    >
                      <option value="">Tous</option>
                      <option 
                        v-for="subUnit in subUnits" 
                        :key="subUnit.value" 
                        :value="subUnit.value"
                      >
                        {{ subUnit.label }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
              <div class="flex-1 pl-0">
                <div class="mb-4">
                  <label for="select-status" class="filter-label">Etat d'occupation</label>
                  <div class="relative">
                    <select 
                      id="select-status" 
                      @change="changeColor" 
                      :class="['filter-select', { 'is-light-blue': filters.status }]" 
                      v-model="filters.status" 
                      data-name="status"
                    >
                      <option value="">Tous</option>
                      <option value="vacant">Vacant</option>
                      <option value="occupé">Occupé</option>
                    </select>
                  </div>
                </div>
                <div class="mb-4">
                  <label for="select-group-owner" class="filter-label">Mandat gpr</label>
                  <div class="relative">
                    <select 
                      id="select-group-owner" 
                      @change="changeColor" 
                      :class="['filter-select', { 'is-light-blue': filters.groupOwner }]" 
                      data-name="groupOwner" 
                      v-model="filters.groupOwner"
                    >
                      <option value="">Tous</option>
                      <option 
                        v-for="groupOwner in groupOwners" 
                        :key="groupOwner.value" 
                        :value="groupOwner.value"
                      >
                        {{ groupOwner.label }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="mb-4">
                  <label for="select-unit" class="filter-label">Unité</label>
                  <div class="relative">
                    <select 
                      id="select-unit" 
                      @change="changeColor" 
                      :class="['filter-select', { 'is-light-blue': filters.unit }]" 
                      data-name="unit" 
                      v-model="filters.unit"
                    >
                      <option value="">Tous</option>
                      <option 
                        v-for="unit in units" 
                        :key="unit.value" 
                        :value="unit.value"
                      >
                        {{ unit.label }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="flex justify-center">
        <button type="submit" class="filter-submit-button" data-component="Widget/Button">Valider</button>
      </div>
    </form>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import moment from 'moment'
import CloseButton from '@/components/Widget/CloseButton.vue'
import dexieService from '@/services/dexieService'
import { saveFilters, loadFilters, getFiltersWithDefaults, DEFAULT_FILTERS } from '@/services/filterStorageService'

export default {
  name: 'ListFilterView',
  components: {
    CloseButton
  },
  data() {
    return {
      filters: {
        nsil: '',
        team: '',
        manager: '',
        owner: '',
        groupOwner: '',
        assistance: '',
        city: '',
        propertyType: '',
        status: '',
        unit: '',
        subUnit: '',
        hasPlanification: false,
        hasNotPlanification: false,
        hasHorsScaprim: false
      },
      managers: [],
      teams: [],
      owners: [],
      groupOwners: [],
      assistances: [],
      propertyTypes: [],
      units: [],
      subUnits: [],
      isPlanificationOpen: false,
      startPlanificationAt: '',
      endPlanificationAt: '',
      startScheduledVisitAt: '',
      endScheduledVisitAt: ''
    }
  },
  computed: {
    ...mapState('list', ['currentFilters'])
  },
  mounted() {
    this.loadCurrentFilters()
    this.loadDataFromIndexedDB()
  },
  methods: {
    ...mapActions('list', ['setFilters']),
    
    loadCurrentFilters() {
      // Charger les filtres depuis le localStorage
      const subject = this.$route.query.subject || 'PropertyPortfolio'
      const saved = getFiltersWithDefaults(subject)

      this.filters = {
        nsil: saved.nsil || '',
        team: saved.team || '',
        manager: saved.manager || '',
        owner: saved.owner || '',
        groupOwner: saved.groupOwner || '',
        assistance: saved.assistance || '',
        city: saved.city || '',
        propertyType: saved.propertyType || '',
        status: saved.status || '',
        unit: saved.unit || '',
        subUnit: saved.subUnit || '',
        hasPlanification: saved.hasPlanification === 'ok',
        hasNotPlanification: saved.hasNotPlanification === 'ok',
        hasHorsScaprim: saved.hasHorsScaprim === 'ok'
      }

      this.startPlanificationAt = saved.startPlanificationAt || ''
      this.endPlanificationAt = saved.endPlanificationAt || ''
      this.startScheduledVisitAt = saved.startScheduledVisitAt || ''
      this.endScheduledVisitAt = saved.endScheduledVisitAt || ''

      if (this.filters.hasPlanification && !this.isPlanificationOpen) {
        this.isPlanificationOpen = true
      }
    },
    
    async loadDataFromIndexedDB() {
      try {
        
        // Charger les équipes (teams)
        const teamsData = await dexieService.getTeams()
        this.teams = teamsData
          .filter(team => team.enabled !== false)
          .map(team => ({
            value: team.id,
            label: team.name
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
        
        // Charger les gestionnaires (managers depuis users)
        const usersData = await dexieService.getUsers()
        this.managers = usersData
          .filter(user => user.enabled !== false)
          .map(user => ({
            value: user.id,
            label: user.fullName || `${user.firstName} ${user.lastName}`
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
        
        // Charger les propriétaires légaux (legalOwners)
        const ownersData = await dexieService.db.table('legalOwners').toArray()
        this.owners = ownersData
          .map(owner => ({
            value: owner.id,
            label: owner.name
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
        
        // Charger les mandants gestionnaires (managerMandators)
        const groupOwnersData = await dexieService.db.table('managerMandators').toArray()
        this.groupOwners = groupOwnersData
          .map(mandator => ({
            value: mandator.id,
            label: mandator.name
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
        
        // Charger les mandants d'assistance (assistanceMandators)
        const assistancesData = await dexieService.db.table('assistanceMandators').toArray()
        this.assistances = assistancesData
          .map(mandator => ({
            value: mandator.id,
            label: mandator.name
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
        
        // Charger les types de propriété depuis le dictionnaire
        const propertyTypeDict = await dexieService.getDictionary('property_type')
        if (propertyTypeDict && typeof propertyTypeDict === 'object') {
          this.propertyTypes = Object.entries(propertyTypeDict)
            .map(([key, value]) => ({
              value: key,
              label: value
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
        }
        
        // Charger les unités depuis le dictionnaire
        const unitDict = await dexieService.getDictionary('unit')
        if (unitDict && typeof unitDict === 'object') {
          this.units = Object.entries(unitDict)
            .map(([key, value]) => ({
              value: key,
              label: value
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
        }
        
        // Charger les sous-unités depuis le dictionnaire
        const subUnitDict = await dexieService.getDictionary('sub_unit')
        if (subUnitDict && typeof subUnitDict === 'object') {
          this.subUnits = Object.entries(subUnitDict)
            .map(([key, value]) => ({
              value: key,
              label: value
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
        }
        
      } catch (error) {
        console.error('Erreur lors du chargement des données depuis IndexedDB:', error)
      }
    },
    
    changeColor(event) {
      if (event.target.value) {
        event.target.classList.add('is-light-blue')
      } else {
        event.target.classList.remove('is-light-blue')
      }
    },
    
    togglePlanification() {
      this.isPlanificationOpen = !this.isPlanificationOpen
    },
    
    changeStartPlanification(event) {
      this.startPlanificationAt = this.parseUTCDate(event.target.value)
    },
    
    changeEndPlanification(event) {
      this.endPlanificationAt = this.parseUTCDate(event.target.value)
    },
    
    resetStartPlanification() {
      this.startPlanificationAt = ''
      document.querySelector('#start-planification').value = ''
    },
    
    resetEndPlanification() {
      this.endPlanificationAt = ''
      document.querySelector('#end-planification').value = ''
    },
    
    changeStartScheduledVisitAt(event) {
      this.startScheduledVisitAt = this.parseUTCDate(event.target.value)
    },
    
    changeEndScheduledVisitAt(event) {
      this.endScheduledVisitAt = this.parseUTCDate(event.target.value)
    },
    
    resetStartScheduledVisitAt() {
      this.startScheduledVisitAt = ''
      document.querySelector('#start-scheduledvisitat').value = ''
    },
    
    resetEndScheduledVisitAt() {
      this.endScheduledVisitAt = ''
      document.querySelector('#end-scheduledvisitat').value = ''
    },
    
    parseUTCDate(date) {
      if (!date) return ''
      const isoDate = `${date}T00:00:00Z`
      return moment(isoDate).toISOString()
    },
    
    formatDate(date) {
      if (!date) return ''
      return moment(date).format('DD/MM/YYYY')
    },
    
    formatDateForInput(date) {
      if (!date) return ''
      return moment(date).format('YYYY-MM-DD')
    },
    
    submit(event) {
      event.preventDefault()
      
      this.offlineSubmit()
    },
    
    onlineSubmit() {
      // Construire l'objet de filtres complet
      const filters = { ...this.filters }
      
      // Ajouter les dates de planification si nécessaire
      if (filters.hasPlanification) {
        filters.startPlanificationAt = this.startPlanificationAt
        filters.endPlanificationAt = this.endPlanificationAt
      }
      
      filters.startScheduledVisitAt = this.startScheduledVisitAt
      filters.endScheduledVisitAt = this.endScheduledVisitAt
      
      // Construire les paramètres de requête
      const query = {}
      
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          if (key === 'hasPlanification' || key === 'hasNotPlanification' || key === 'hasHorsScaprim') {
            query[key] = 'ok'
          } else {
            query[key] = filters[key]
          }
        }
      })
      
      // Mettre à jour le store
      this.setFilters(filters)
      
      // Naviguer vers la liste avec les nouveaux filtres
      this.$router.push({
        name: 'PropertyPortfolio',
        query
      })
    },
    
    offlineSubmit() {
      // Déterminer la route cible selon le subject passé en query
      const subject = this.$route.query.subject || 'PropertyPortfolio'
      const SUBJECT_TO_PATH = {
        PropertyPortfolio: '/parc-immobilier',
        MyVisites: '/visites',
        ActionsList: '/actions'
      }
      const targetPath = SUBJECT_TO_PATH[subject] || '/parc-immobilier'

      // Construire l'objet de filtres complet avec toutes les valeurs
      const filters = { ...this.filters }

      // Ajouter les dates de planification si nécessaire
      if (filters.hasPlanification) {
        if (this.startPlanificationAt) {
          filters.startPlanificationAt = this.startPlanificationAt
        }
        if (this.endPlanificationAt) {
          filters.endPlanificationAt = this.endPlanificationAt
        }
      }

      // Ajouter les dates de visite programmée
      if (this.startScheduledVisitAt) {
        filters.startScheduledVisitAt = this.startScheduledVisitAt
      }
      if (this.endScheduledVisitAt) {
        filters.endScheduledVisitAt = this.endScheduledVisitAt
      }

      // Construire l'objet de filtres pour localStorage (booléens → 'ok')
      const queryObject = {}
      Object.keys(filters).forEach(key => {
        const value = filters[key]
        if (key === 'hasPlanification' || key === 'hasNotPlanification' || key === 'hasHorsScaprim') {
          if (value === true) {
            queryObject[key] = 'ok'
          }
        } else if (key === 'team') {
          // Toujours sauvegarder 'team', même vide : '' = "l'utilisateur a choisi Tous"
          // (permet de distinguer "jamais défini" de "volontairement vidé")
          queryObject[key] = value || ''
        } else {
          if (value && value !== '') {
            queryObject[key] = value
          }
        }
      })

      // Conserver les filtres invisibles (non gérés par le formulaire, ex: excludeType)
      // pour éviter qu'une validation du formulaire n'efface silencieusement ces filtres.
      const INVISIBLE_FILTER_KEYS = ['excludeType']
      const currentSaved = loadFilters(subject) || {}
      const subjectDefaults = DEFAULT_FILTERS[subject] || {}
      INVISIBLE_FILTER_KEYS.forEach(key => {
        // Priorité : valeur déjà sauvegardée > valeur par défaut
        if (currentSaved[key] !== undefined) {
          queryObject[key] = currentSaved[key]
        } else if (subjectDefaults[key] !== undefined) {
          queryObject[key] = subjectDefaults[key]
        }
      })

      // Sauvegarder les filtres dans localStorage
      saveFilters(subject, queryObject)

      // Mettre à jour le store
      this.setFilters(filters)

      // Navigation vers la liste SANS filtres dans l'URL
      // (les filtres sont lus depuis localStorage par les vues de liste)
      this.$router.replace(targetPath)
    },
    
    goBack() {
      this.$router.go(-1)
    }
  }
}
</script>

<style lang="scss" scoped>
@import './ListFilter.scss';
</style>
